import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { createWithEqualityFn } from "zustand/traditional";
import type { Customer, DeviceReplacement, SessionSummary } from "../api";

export interface CustomerState {
    customer?: Customer;
    deviceReplacement?: DeviceReplacement;
    previousSummary?: SessionSummary;
    isEditingVerificationInput: boolean;
    isCustomerVerificationComplete: boolean;
}

export interface VerifiedCustomerStore {
    verifiedCustomers: Record<string, CustomerState>;
    // Preserves unverified customer data temporarily if not yet confirmed before switching to another session
    unverifiedCustomerData: Record<string, Partial<Customer>>;

    getVerifiedCustomer: (sessionId: string) => CustomerState;
    getUnverifiedCustomerData: (sessionId: string) => Partial<Customer>;
    setPreviousSummary: (sessionId: string, previousSummary?: SessionSummary) => void;
    setCustomer: (
        sessionId: string,
        customer?: Partial<Customer & { mdn: string }>,
        deviceReplacement?: DeviceReplacement,
        previousSummary?: SessionSummary,
    ) => void;
    setUnverifiedCustomerData: (sessionId: string, customerData: Partial<Customer>) => void;
    setIsEditingVerificationInput: (sessionId: string, isEditing: boolean) => void;
    setIsCustomerVerificationComplete: (sessionId: string, isComplete: boolean) => void;
    reset: (sessionId: string) => void;
}

export const useVerifiedCustomerStore = createWithEqualityFn<VerifiedCustomerStore>()(
    persist(
        immer(
            devtools(
                (set, get) => ({
                    verifiedCustomers: {},
                    unverifiedCustomerData: {},
                    getVerifiedCustomer: (sessionId: string): CustomerState => {
                        return (
                            get().verifiedCustomers[sessionId] ?? {
                                customer: undefined,
                                deviceReplacement: undefined,
                                previousSummary: undefined,
                                isEditingVerificationInput: true,
                                isCustomerVerificationComplete: false,
                            }
                        );
                    },
                    getUnverifiedCustomerData: (sessionId: string): Partial<Customer> => {
                        return get().unverifiedCustomerData[sessionId] ?? {};
                    },
                    setPreviousSummary: (sessionId: string, previousSummary?: SessionSummary) =>
                        set(
                            (state) => {
                                const currentVerifiedCustomer = state.getVerifiedCustomer(sessionId);
                                state.verifiedCustomers[sessionId] = {
                                    ...currentVerifiedCustomer,
                                    previousSummary,
                                };
                            },
                            false,
                            "setPreviousSummary",
                        ),
                    setCustomer: (
                        sessionId: string,
                        customer?: Partial<Customer & { mdn: string }>,
                        deviceReplacement?: DeviceReplacement,
                        previousSummary?: SessionSummary,
                    ) =>
                        set(
                            (state) => {
                                const currentVerifiedCustomer = state.getVerifiedCustomer(sessionId);
                                state.verifiedCustomers[sessionId] = {
                                    ...currentVerifiedCustomer,
                                    customer: {
                                        ...currentVerifiedCustomer.customer,
                                        ...customer,
                                    },
                                    deviceReplacement,
                                    previousSummary,
                                };
                                delete state.unverifiedCustomerData[sessionId];
                            },
                            false,
                            "setCustomer",
                        ),
                    setUnverifiedCustomerData: (sessionId: string, customerData: Partial<Customer>) => {
                        set(
                            (state) => {
                                state.unverifiedCustomerData[sessionId] = {
                                    ...state.unverifiedCustomerData[sessionId],
                                    ...customerData,
                                };
                            },
                            false,
                            "setUnverifiedCustomerData",
                        );
                    },
                    setIsEditingVerificationInput: (sessionId: string, isEditing: boolean) =>
                        set(
                            (state) => {
                                const currentVerifiedCustomer = state.getVerifiedCustomer(sessionId);
                                state.verifiedCustomers[sessionId] = {
                                    ...currentVerifiedCustomer,
                                    isEditingVerificationInput: isEditing,
                                };
                            },
                            false,
                            "setIsCustomerVerificationComplete",
                        ),
                    setIsCustomerVerificationComplete: (sessionId: string, isComplete: boolean) =>
                        set(
                            (state) => {
                                const currentVerifiedCustomer = state.getVerifiedCustomer(sessionId);
                                state.verifiedCustomers[sessionId] = {
                                    ...currentVerifiedCustomer,
                                    isCustomerVerificationComplete: isComplete,
                                };
                                if (isComplete) {
                                    delete state.unverifiedCustomerData[sessionId];
                                }
                            },
                            false,
                            "setIsCustomerVerificationComplete",
                        ),
                    reset: (sessionId: string) =>
                        set(
                            (state) => {
                                delete state.verifiedCustomers[sessionId];
                                delete state.unverifiedCustomerData[sessionId];
                            },
                            false,
                            "reset",
                        ),
                }),
                {
                    enabled: import.meta.env.VITE_ENV_TYPE !== "production",
                    store: "verified-customer-store",
                    name: "workspace/customer-info",
                },
            ),
        ),
        { name: "verified-customer-store", skipHydration: true },
    ),
);
